// Report-param resolution for the info-view compare-items block, ported from the coffee view
// (the format defaults from initialize plus importReportParams). The block state is a merge of
// the cts<key> report-param section, the block format and the block defaults, in that order.

import { removeTagsFromText } from '@aytm/app/stats/page/components/lib/utils'

import {
  ANALYSIS_MODES,
  MAIN_ANALYSIS_MODE,
  MAIN_PRESET_ID,
  PRESETS,
  getItemsIndexes
} from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/compare-items-utils'

const FULL_TEXT_LENGTH = 17

// showImages / fullTexts are opt-in per block; when the format leaves them unset the block
// decides from its own batches whether the toggles make sense at all.
export const resolveFormat = (format, batches = []) => {
  const needsShowImages = undefined === format?.showImages
  const needsFullTexts = undefined === format?.fullTexts

  if (!needsShowImages && !needsFullTexts) return format

  const resolved = { ...(format || {}) }

  if (needsShowImages) {
    resolved.showImages = batches.some(batch => undefined !== batch.image)
  }
  if (needsFullTexts) {
    resolved.fullTexts = batches.some(batch => FULL_TEXT_LENGTH < removeTagsFromText(batch.text || '').length)
  }

  return resolved
}

// Resolves the state a freshly mounted (or re-seeded) block starts from, given its own
// report-param section. `needsUpdate` marks a section that no longer matches the data, so the
// caller writes the corrected state back.
export const resolveReportParams = ({
  section = {},
  format = {},
  block,
  items = [],
  attributes,
  mainAttributePosition = -1
} = {}) => {
  const state = {
    concepts: undefined !== section.concepts ? section.concepts : [],
    preset:
      undefined !== section.preset ? section.preset : undefined !== format.preset ? format.preset : MAIN_PRESET_ID,
    attribute:
      undefined !== section.attribute
        ? section.attribute
        : undefined !== format.attribute
          ? format.attribute
          : mainAttributePosition,
    reverse: undefined !== section.reverse ? section.reverse : undefined !== format.reverse ? format.reverse : false,
    analysisMode:
      undefined !== section.analysisMode
        ? section.analysisMode
        : undefined !== format.analysisMode
          ? format.analysisMode
          : block?.analysisMode || MAIN_ANALYSIS_MODE,
    showImages: format.showImages && undefined !== section.showImages ? section.showImages : format.showImages,
    // unlike showImages, which is gated on the block having any, this is only a default -- the
    // researcher is always offered the toggle, so whatever they chose is what is read back
    fullTexts: undefined !== section.fullTexts ? section.fullTexts : format.fullTexts
  }

  let needsUpdate = false

  // A stored value the table cannot draw falls back to its default and is written back. An unknown preset or
  // mode can only come from an edited param; an attribute or a concept can be one the researcher has since
  // deleted from a Concept Lab or Tracker Lab, and a concept that no longer resolves would break the re-derive.
  if ('' !== state.preset && !PRESETS.some(preset => preset.id === state.preset)) {
    state.preset = MAIN_PRESET_ID
    needsUpdate = true
  }

  if (!ANALYSIS_MODES.includes(state.analysisMode)) {
    state.analysisMode = MAIN_ANALYSIS_MODE
    needsUpdate = true
  }

  if (Array.isArray(attributes) && 0 <= state.attribute && !attributes.some(a => a.position === state.attribute)) {
    state.attribute = mainAttributePosition
    needsUpdate = true
  }

  const resolvable = state.concepts.filter(position => items.some(item => item.position === position))
  if (resolvable.length !== state.concepts.length) {
    state.concepts = resolvable
    needsUpdate = true
  }

  if (0 <= state.attribute) {
    const concepts = getItemsIndexes(items, state.concepts, state.preset, state.attribute, state.reverse).map(
      index => items[index].position
    )

    if (0 < state.concepts.length && JSON.stringify(concepts) !== JSON.stringify(state.concepts)) {
      state.concepts = concepts
      needsUpdate = true
    }
  }

  if (0 < state.concepts.length && '' !== state.preset) {
    state.concepts = []
    needsUpdate = true
  }

  return { state, needsUpdate }
}
